

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'
import { base_url } from '../../utils/config'

// fetch the “primary” shipping address (earliest one)
export const get_shipping_address = createAsyncThunk(
  'customer/get_shipping_address',
  async (customerId, { getState, rejectWithValue }) => {
    const { token } = getState().auth
    try {
      const { data } = await axios.get(
        `${base_url}/api/customers/shipping/${customerId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      return data.shippingAddress
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed' })
    }
  }
)

// fetch all addresses
export const get_all_addresses = createAsyncThunk(
  'customer/get_all_addresses',
  async (customerId, { getState, rejectWithValue }) => {
    const { token } = getState().auth
    try {
      const { data } = await axios.get(
        `${base_url}/api/customers/shipping/all/${customerId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      return data.shippingAddresses
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed' })
    }
  }
)

// add a new address
export const add_new_address = createAsyncThunk(
  'customer/add_new_address',
  async ({ customerId, addressData }, { getState, rejectWithValue }) => {
    const { token } = getState().auth
    try {
      const { data } = await axios.post(
        `${base_url}/api/customers/shipping`,
        { customerId, addressData },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      return data.shippingAddresses
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed' })
    }
  }
)

// update an existing address
export const update_shipping_address = createAsyncThunk(
  'customer/update_shipping_address',
  async ({ addressId, addressData }, { getState, rejectWithValue }) => {
    const { token } = getState().auth
    try {
      const { data } = await axios.put(
        `${base_url}/api/customers/shipping`,
        { addressId, addressData },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      return data.shippingAddress
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed' })
    }
  }
)

// fetch the customer’s “selected” address
export const get_selected_address = createAsyncThunk(
  'customer/get_selected_address',
  async (customerId, { getState, rejectWithValue }) => {
    const { token } = getState().auth
    try {
      const { data } = await axios.get(
        `${base_url}/api/customers/selected-address/${customerId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      return data.selectedAddress
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: 'Failed to fetch selected address' }
      )
    }
  }
)

// set which address is “selected”
export const set_selected_address = createAsyncThunk(
  'customer/set_selected_address',
  async ({ customerId, addressId }, { getState, rejectWithValue }) => {
    const { token } = getState().auth
    try {
      const { data } = await axios.put(
        `${base_url}/api/customers/selected-address`,
        { customerId, addressId },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      return data.selectedAddress
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: 'Failed to select address' }
      )
    }
  }
)

// fetch a Borzo quote → returns quoteId and price
export const get_one_day_quote = createAsyncThunk(
  'customer/get_one_day_quote',
  async ({ city, area, phone }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `${base_url}/api/borzo/quote`,
        { params: { city, area, phone } }
      )
      // data: { quoteId, price }
      return data
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || 'Failed to fetch one-day quote'
      )
    }
  }
)

// ✨ NEW: request hyper-delivery (seller-driven)
//    Now sends both the original Borzo quote and your 15%-markup price
export const request_hyper_delivery = createAsyncThunk(
  'customer/request_hyper_delivery',
  async (
    { orderId, city, area, phone, quotePrice, markupPrice },
    { rejectWithValue }
  ) => {
    try {
      const payload = { orderId, city, area, phone, quotePrice, markupPrice }
      const { data } = await axios.post(
        `${base_url}/api/borzo/request-hyper-delivery`,
        payload
      )
      // data: { message, request }
      return data.request
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || 'Failed to request hyper delivery'
      )
    }
  }
)

// fetch Borzo booking status by orderId
export const get_borzo_status = createAsyncThunk(
  'customer/get_borzo_status',
  async (orderId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `${base_url}/api/borzo/booking/${orderId}`
      )
      return data
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || 'Failed to fetch Borzo status'
      )
    }
  }
)

const slice = createSlice({
  name: 'customer',
  initialState: {
    shippingAddress:    {},
    shippingAddresses:  [],
    selectedAddress:    {},

    // one-day quote
    oneDayQuote:        { quoteId: null, price: null },
    oneDayLoading:      false,
    oneDayError:        null,

    // ✨ hyper-delivery request
    hyperDeliveryRequest:        null,
    requestHyperDeliveryLoading: false,
    requestHyperDeliveryError:   null,

    // Borzo status
    borzoBooking:          null,
    getBorzoStatusLoading: false,
    getBorzoStatusError:   null,

    loading:            false,
    error:              null
  },
  reducers: {},
  extraReducers: builder =>
    builder
      // get single
      .addCase(get_shipping_address.pending, state => {
        state.loading = true
      })
      .addCase(get_shipping_address.fulfilled, (state, { payload }) => {
        state.loading = false
        state.shippingAddress = payload
      })
      .addCase(get_shipping_address.rejected, (state, { payload }) => {
        state.loading = false
        state.error = payload?.message || 'Failed to fetch shipping address'
      })

      // get all
      .addCase(get_all_addresses.pending, state => {
        state.loading = true
      })
      .addCase(get_all_addresses.fulfilled, (state, { payload }) => {
        state.loading = false
        state.shippingAddresses = payload
      })
      .addCase(get_all_addresses.rejected, (state, { payload }) => {
        state.loading = false
        state.error = payload?.message || 'Failed to fetch addresses'
      })

      // add new
      .addCase(add_new_address.pending, state => {
        state.loading = true
      })
      .addCase(add_new_address.fulfilled, (state, { payload }) => {
        state.loading = false
        state.shippingAddresses = payload
      })
      .addCase(add_new_address.rejected, (state, { payload }) => {
        state.loading = false
        state.error = payload?.message || 'Failed to add address'
      })

      // update existing
      .addCase(update_shipping_address.pending, state => {
        state.loading = true
      })
      .addCase(update_shipping_address.fulfilled, (state, { payload }) => {
        state.loading = false
        state.shippingAddress = payload
        state.shippingAddresses = state.shippingAddresses.map(a =>
          a._id === payload._id ? payload : a
        )
      })
      .addCase(update_shipping_address.rejected, (state, { payload }) => {
        state.loading = false
        state.error = payload?.message || 'Failed to update address'
      })

      // get selected
      .addCase(get_selected_address.pending, state => {
        state.loading = true
      })
      .addCase(get_selected_address.fulfilled, (state, { payload }) => {
        state.loading = false
        state.selectedAddress = payload
      })
      .addCase(get_selected_address.rejected, (state, { payload }) => {
        state.loading = false
        state.error = payload?.message || 'Failed to fetch selected address'
      })

      // select address
      .addCase(set_selected_address.pending, state => {
        state.loading = true
      })
      .addCase(set_selected_address.fulfilled, (state, { payload }) => {
        state.loading = false
        state.selectedAddress = payload
      })
      .addCase(set_selected_address.rejected, (state, { payload }) => {
        state.loading = false
        state.error = payload?.message || 'Failed to select address'
      })

      // one-day quote
      .addCase(get_one_day_quote.pending, state => {
        state.oneDayLoading = true
        state.oneDayError   = null
      })
      .addCase(get_one_day_quote.fulfilled, (state, { payload }) => {
        state.oneDayLoading = false
        state.oneDayQuote   = {
          quoteId: payload.quoteId,
          price:   payload.price
        }
      })
      .addCase(get_one_day_quote.rejected, (state, { payload }) => {
        state.oneDayLoading = false
        state.oneDayError   = payload
      })

      // request hyper-delivery
      .addCase(request_hyper_delivery.pending, state => {
        state.requestHyperDeliveryLoading = true
        state.requestHyperDeliveryError   = null
      })
      .addCase(request_hyper_delivery.fulfilled, (state, { payload }) => {
        state.requestHyperDeliveryLoading = false
        state.hyperDeliveryRequest        = payload
      })
      .addCase(request_hyper_delivery.rejected, (state, { payload }) => {
        state.requestHyperDeliveryLoading = false
        state.requestHyperDeliveryError   = payload
      })

      // fetch Borzo status
      .addCase(get_borzo_status.pending, state => {
        state.getBorzoStatusLoading = true
        state.getBorzoStatusError   = null
      })
      .addCase(get_borzo_status.fulfilled, (state, { payload }) => {
        state.getBorzoStatusLoading = false
        state.borzoBooking          = payload
      })
      .addCase(get_borzo_status.rejected, (state, { payload }) => {
        state.getBorzoStatusLoading = false
        state.getBorzoStatusError   = payload
      }),
})

export default slice.reducer
